import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject, input, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, map } from 'rxjs';
import { FilterEventService } from 'src/app/services/filter-event.service';
import { AtomicBlueprintSupported } from '../../shared/atomic/atomic.component';
import { BoTable, GenericTable } from 'src/openapi-client';
import { TranslocoModule } from '@ngneat/transloco';
import iconLibrary from 'src/app/common/iconLibrary';
import { SortArrowsComponent } from './sort-arrows/sort-arrows.component';
import { RowCompareFn, TableSorter } from './table-sorter';

const STORAGE_KEY = 'wct.generic-table.hidden-columns';

interface ColumnInfo {
  index: number;
  label: string;
}

function readHidden(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : []);
  } catch {
    return new Set();
  }
}

function writeHidden(hidden: Set<string>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...hidden]));
  } catch {
    // storage unavailable (private mode, quota) - selection just won't persist
  }
}

function toPlainText(html: string | undefined): string {
  return (new DOMParser().parseFromString(html ?? '', 'text/html').body.textContent ?? '').replace(/\s+/g, ' ').trim();
}

type Row = BoTable['rows'][number];

function toNumber(text: string): number | null {
  const stripped = text.replace(/[\s%€]/g, '');
  const normalized = stripped.includes(',') ? stripped.replace(/\./g, '').replace(',', '.') : stripped;
  return /^[-+]?\d+(\.\d+)?$/.test(normalized) ? Number(normalized) : null;
}

function cellText(row: Row, column: number): string {
  let index = 0;
  for (const cell of row.cells) {
    if (index === column) {
      return toPlainText(cell.content);
    }
    index += cell.colspan ?? 1;
  }
  return '';
}

@Component({
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [TranslocoModule, SortArrowsComponent],
  selector: 'wct-generic-table',
  templateUrl: './generic-table.component.html',
  styleUrl: './generic-table.component.scss',
})
export class GenericTableComponent implements AtomicBlueprintSupported<GenericTable> {
  data = input.required<GenericTable>();

  protected readonly iconLibrary = iconLibrary;
  protected readonly hidden = signal<Set<string>>(readHidden());
  protected readonly search = signal('');
  protected readonly rowSearchText = signal('');

  private readonly filterEvents = inject(FilterEventService);
  private readonly rowTerm = toSignal(
    toObservable(this.rowSearchText).pipe(
      debounceTime(200),
      map(v => v.trim().toLowerCase()),
      distinctUntilChanged()
    ),
    { initialValue: '' }
  );

  constructor() {
    effect(() => writeHidden(this.hidden()));
  }

  private readonly rows = computed(() => (this.data().content as BoTable).rows ?? []);

  protected readonly columns = computed<ColumnInfo[]>(() => {
    const headerCells = this.rows()[0]?.cells ?? [];
    const result: ColumnInfo[] = [];
    let index = 0;
    for (const cell of headerCells) {
      const span = cell.colspan ?? 1;
      const label = toPlainText(cell.content);
      if (label && span === 1) {
        result.push({ index, label });
      }
      index += span;
    }
    return result;
  });

  protected readonly filteredColumns = computed(() => {
    const term = this.search().trim().toLowerCase();
    return this.columns().filter(col => col.label.toLowerCase().includes(term));
  });

  protected readonly hiddenIndexes = computed(() => {
    const hidden = this.hidden();
    return new Set(this.columns().filter(col => hidden.has(col.label)).map(col => col.index));
  });

  private readonly rowTexts = computed(() =>
    this.rows().map(row => row.cells.map(cell => toPlainText(cell.content)).join(' ').toLowerCase())
  );

  private readonly matchingRows = computed(() => {
    const term = this.rowTerm();
    if (!term) {
      return this.rows();
    }
    const texts = this.rowTexts();
    return this.rows().filter((_, i) => i === 0 || texts[i].includes(term));
  });

  private readonly compareFns = computed(() => {
    const body = this.rows().slice(1);
    const fns = new Map<number, RowCompareFn<Row>>();
    for (const { index } of this.columns()) {
      const first = body.map(row => cellText(row, index)).find(text => text !== '');
      if (first === undefined) {
        continue;
      }
      const num = (row: Row) => toNumber(cellText(row, index));
      fns.set(
        index,
        toNumber(first) !== null
          ? (a, b) => (num(a) ?? Infinity) - (num(b) ?? Infinity)
          : (a, b) => cellText(a, index).localeCompare(cellText(b, index), 'de', { sensitivity: 'base' })
      );
    }
    return fns;
  });

  private readonly sortState = signal<{ column: number | null; ascending: boolean }>({ column: null, ascending: true });

  protected readonly sorter = computed(() => {
    const sorter = new TableSorter(this.compareFns());
    const { column, ascending } = this.sortState();
    sorter.sortedColumnIndex = column !== null && sorter.canSort(column) ? column : null;
    sorter.ascending = ascending;
    return sorter;
  });

  private readonly sortedRows = computed(() => {
    const [header, ...body] = this.matchingRows();
    return header ? [header, ...this.sorter().apply(body)] : [];
  });

  protected readonly noResults = computed(() => !!this.rowTerm() && this.matchingRows().length <= 1);

  protected readonly visibleRows = computed(() => {
    const hiddenIndexes = this.hiddenIndexes();
    return this.sortedRows().map(row => {
      let index = 0;
      return row.cells
        .map(cell => {
          const start = index;
          index += cell.colspan ?? 1;
          return { cell, start };
        })
        .filter(({ cell, start }) => (cell.colspan ?? 1) > 1 || !hiddenIndexes.has(start));
    });
  });

  /** none -> ascending -> descending -> none */
  protected sort(column: number): void {
    const next = this.sorter().clone();
    next.sort(column);
    this.sortState.set({ column: next.sortedColumnIndex, ascending: next.ascending });
  }

  protected toggle(label: string, visible: boolean): void {
    const next = new Set(this.hidden());
    if (visible) {
      next.delete(label);
    } else {
      next.add(label);
    }
    this.hidden.set(next);
  }

  protected onRowSearch(event: Event): void {
    this.rowSearchText.set((event.target as HTMLInputElement).value ?? '');
  }

  protected clearRowSearch(): void {
    this.rowSearchText.set('');
  }

  protected resetSearchAndFilters(): void {
    this.clearRowSearch();
    this.filterEvents.resetRequested();
  }

  protected onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value ?? '');
  }
}
